import { useState } from 'react';
import { Plus, Shield, Search, X, Pin, MessageSquare, Eye, Users, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ForumTopicList } from './ForumTopicList';
import { ForumTopicDetail } from './ForumTopicDetail';
import { ForumNewTopicForm } from './ForumNewTopicForm';
import { ForumAdminPanel } from './ForumAdminPanel';
import { useCategories, useTopics, useTopic, useSearchTopics } from '@/hooks/useForum';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

export function ForumContent() {
  const { user, isAdmin } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: topicsData, isLoading: topicsLoading, refetch: refetchTopics } = useTopics(
    selectedCategory ?? undefined,
    page,
    categories
  );
  const { data: searchData, isLoading: searchLoading } = useSearchTopics(searchQuery);
  const { data: topicDetail, isLoading: topicLoading, refetch: refetchTopic } = useTopic(
    selectedTopicId ?? 0,
    true
  );

  const isSearching = searchQuery.length >= 2;
  const displayTopics = isSearching ? searchData : topicsData;

  const handleSelectTopic = (topicId: number) => {
    setSelectedTopicId(topicId);
    setShowNewTopicForm(false);
  };

  const handleBackToList = () => {
    setSelectedTopicId(null);
    refetchTopics();
  };

  const handleNewTopicSuccess = (topicId: number) => {
    setShowNewTopicForm(false);
    setSelectedTopicId(topicId);
    refetchTopics();
  };

  const handleReplySuccess = () => {
    refetchTopic();
  };

  const subcategoriesOnly = categories?.filter(c => c.parent_id !== null) || [];
  const totalTopics = displayTopics?.total || 0;
  const totalPages = Math.ceil(totalTopics / (displayTopics?.limit || 10));

  // Show admin panel
  if (showAdminPanel) {
    return (
      <div className="min-h-[600px] bg-muted/30">
        <div className="max-w-6xl mx-auto px-3 py-4">
          <div className="flex justify-end mb-4">
            <Button 
              variant="secondary"
              size="sm"
              onClick={() => setShowAdminPanel(false)}
              className="gap-1.5"
            >
              <Shield className="h-3.5 w-3.5" />
              Stäng admin
            </Button>
          </div>
          <ForumAdminPanel />
        </div>
      </div>
    );
  }

  // Show topic detail
  if (selectedTopicId && topicDetail) {
    return (
      <div className="min-h-[600px] bg-muted/30">
        <div className="max-w-6xl mx-auto px-3 py-4">
          <ForumTopicDetail
            topic={topicDetail.topic}
            replies={topicDetail.replies}
            onBack={handleBackToList}
            onReplySuccess={handleReplySuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[600px] bg-muted/30">
      {/* Classic header */}
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-3 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-foreground/20 rounded flex items-center justify-center">
                <span className="text-2xl">🧩</span>
              </div>
              <div>
                <h1 className="text-xl font-bold">Korsordsforum</h1>
                <p className="text-primary-foreground/70 text-xs">Sveriges största korsordscommunity</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <span className="text-sm text-primary-foreground/80">Välkommen, {user.email?.split('@')[0]}!</span>
              ) : (
                <span className="text-sm text-primary-foreground/70">Välkommen, gäst!</span>
              )}
              {isAdmin && (
                <Button 
                  size="sm" 
                  variant="secondary" 
                  className="bg-primary-foreground/20 hover:bg-primary-foreground/30 border-0 text-primary-foreground"
                  onClick={() => setShowAdminPanel(true)}
                >
                  <Shield className="h-4 w-4 mr-1.5" />
                  Admin
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 py-4">
        <div className="flex gap-4">
          {/* Sidebar - Avdelningar */}
          <aside className="w-48 flex-shrink-0 hidden md:block">
            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
              <div className="bg-primary text-primary-foreground px-3 py-2 font-semibold text-sm">
                Avdelningar
              </div>
              <div className="divide-y divide-border">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setPage(1);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex justify-between items-center",
                    selectedCategory === null && "bg-accent text-accent-foreground font-medium"
                  )}
                >
                  <span>Alla avdelningar</span>
                  <span className="text-xs text-muted-foreground">{totalTopics}</span>
                </button>
                {categoriesLoading ? (
                  <div className="p-3 space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-6 w-full" />
                    ))}
                  </div>
                ) : (
                  subcategoriesOnly.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        setPage(1);
                      }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex justify-between items-center",
                        selectedCategory === cat.id && "bg-accent text-accent-foreground font-medium"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary" />
                        <span>{cat.name}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Forum stats */}
            <div className="bg-card rounded-lg shadow-sm border border-border mt-4 overflow-hidden">
              <div className="bg-secondary text-secondary-foreground px-3 py-2 font-semibold text-sm">
                Forum statistik
              </div>
              <div className="p-3 text-xs text-muted-foreground space-y-1">
                <div className="flex justify-between">
                  <span>Trådar:</span>
                  <span className="font-medium text-foreground">{totalTopics}</span>
                </div>
                <div className="flex justify-between">
                  <span>Kategorier:</span>
                  <span className="font-medium text-foreground">{subcategoriesOnly.length}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Search and actions bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Sök i forumet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9 h-9 bg-card border-border"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button 
                size="sm" 
                className="bg-primary hover:bg-primary/90 gap-1.5"
                onClick={() => setShowNewTopicForm(true)}
              >
                <Plus className="h-4 w-4" />
                Skapa ny tråd
              </Button>
            </div>

            {/* Mobile category selector */}
            <div className="md:hidden mb-4">
              <select
                value={selectedCategory ?? ''}
                onChange={(e) => {
                  setSelectedCategory(e.target.value ? Number(e.target.value) : null);
                  setPage(1);
                }}
                className="w-full h-9 px-3 rounded-md border border-border bg-card text-foreground text-sm"
              >
                <option value="">Alla avdelningar</option>
                {subcategoriesOnly.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {showNewTopicForm && subcategoriesOnly.length > 0 ? (
              <ForumNewTopicForm
                categories={subcategoriesOnly}
                onClose={() => setShowNewTopicForm(false)}
                onSuccess={handleNewTopicSuccess}
                parentCategories={categories?.filter(c => c.parent_id === null) || []}
              />
            ) : (
              <>
                {isSearching && (
                  <div className="mb-4 text-sm text-muted-foreground bg-card px-3 py-2 rounded-md border border-border">
                    Sökresultat för "<span className="font-medium text-foreground">{searchQuery}</span>"
                  </div>
                )}

                {/* Thread table */}
                <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary border-b border-border">
                      <tr className="text-left text-secondary-foreground">
                        <th className="px-3 py-2.5 font-medium">Ämne</th>
                        <th className="px-3 py-2.5 font-medium w-20 text-center hidden sm:table-cell">Svar</th>
                        <th className="px-3 py-2.5 font-medium w-44 text-right hidden md:table-cell">Senaste inlägg</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(isSearching ? searchLoading : topicsLoading) ? (
                        [...Array(5)].map((_, i) => (
                          <tr key={i}>
                            <td className="px-3 py-3" colSpan={3}>
                              <div className="space-y-2">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-3 w-1/2" />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : displayTopics?.topics.length === 0 ? (
                        <tr>
                          <td className="px-3 py-8 text-center text-muted-foreground" colSpan={3}>
                            {isSearching 
                              ? 'Inga trådar hittades med din sökning.' 
                              : 'Inga trådar i denna kategori ännu.'}
                            {!isSearching && (
                              <button 
                                onClick={() => setShowNewTopicForm(true)}
                                className="block mx-auto mt-2 text-primary hover:underline"
                              >
                                Starta första diskussionen →
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        displayTopics?.topics.map((topic) => (
                          <tr 
                            key={topic.id} 
                            onClick={() => handleSelectTopic(topic.id)}
                            className="hover:bg-accent/50 transition-colors cursor-pointer"
                          >
                            <td className="px-3 py-3">
                              <div className="flex items-start gap-2">
                                {topic.is_pinned && <Pin className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />}
                                <div className="min-w-0">
                                  <div className="font-medium text-primary hover:underline line-clamp-1">
                                    {topic.title}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-block w-2 h-2 rounded-full bg-primary/60" />
                                    <span className="truncate">{topic.category_name}</span>
                                    <span>•</span>
                                    <span>Av {topic.author_name}</span>
                                    <span className="sm:hidden">• {topic.reply_count} svar</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center hidden sm:table-cell">
                              <span className={cn(
                                "inline-block min-w-[2rem] py-0.5 rounded text-xs font-medium",
                                topic.reply_count > 10 ? "bg-primary/10 text-primary" : "text-muted-foreground"
                              )}>
                                {topic.reply_count}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-right hidden md:table-cell">
                              <div className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(topic.last_reply_at || topic.created_at), { 
                                  addSuffix: true, 
                                  locale: sv 
                                })}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!isSearching && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="text-xs"
                    >
                      « Föregående
                    </Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <Button 
                          key={pageNum}
                          variant="outline" 
                          size="sm" 
                          onClick={() => setPage(pageNum)}
                          className={cn(
                            "text-xs",
                            page === pageNum && "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
                          )}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    {totalPages > 5 && page < totalPages - 2 && (
                      <span className="text-muted-foreground px-2">...</span>
                    )}
                    {totalPages > 5 && page < totalPages - 2 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(totalPages)}
                        className="text-xs"
                      >
                        {totalPages}
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="text-xs"
                    >
                      Nästa »
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}